import { Injectable } from '@angular/core';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, lastValueFrom, take, catchError, of } from 'rxjs';
import { ShopGraphqlService } from 'src/app/core/graphql/shop/shop-graphql.service';
import { TokenService } from 'src/app/core/token/token.service';
import { environment } from 'src/environments/environment';

const cookieConfig: CookieOptions = {
  path: '/',
  secure: !environment.dev,
  sameSite: 'Lax',
  domain: environment.dev ? '' : environment.cookieDomain,
};

export interface CheckoutData {
  checkoutLine: CheckoutLine[];
  marketplaceCheckoutId: string;
  subTotalPrices: number;
  subTotalQuantity: number;
}

export interface CheckoutLine {
  name: string;
  slug: string;
  totalPrice: any;
  lines: any[];
  shippingPrice: any;
}

export interface CheckoutLineItemDescription {
  data: any;
  id: string;
  type: string;
}

export interface CheckoutLineItemDescriptionData {
  text: string;
  type: string;
}

@Injectable({
  providedIn: 'root',
})
export class CartControllerService {
  public _checkoutData: CheckoutData = {
    marketplaceCheckoutId: '',
    checkoutLine: new Array<CheckoutLine>(),
    subTotalPrices: 0,
    subTotalQuantity: 0,
  };
  public _checkoutData$: BehaviorSubject<CheckoutData> = new BehaviorSubject<CheckoutData>(
    this._checkoutData,
  );

  constructor(
    private cookie: CookieService,
    private tokenService: TokenService,
    private shopGrapqhlService: ShopGraphqlService,
  ) {}

  get marketplaceCheckoutId(): string | undefined {
    return this.cookie.get('markectplace-checkout');
  }

  set marketplaceCheckoutId(id: string | undefined) {
    this.cookie.delete('markectplace-checkout');
    if (id) this.cookie.set('markectplace-checkout', id, cookieConfig);
  }

  get checkoutData(): CheckoutData {
    return this._checkoutData;
  }

  set checkoutData(data: CheckoutData) {
    this._checkoutData = data;
    this._checkoutData$.next(this._checkoutData);
  }

  public init() {
    if (this.tokenService.accessToken) {
      this.getMarketplaceCart().then((getMarketplaceCartData) => {
        if (!getMarketplaceCartData) {
          this.createMarketplaceCart();
          return;
        }

        if (this.marketplaceCheckoutId) {
          if (this.marketplaceCheckoutId == getMarketplaceCartData.id) {
            this.checkoutData = this.convertResponesToCheckoutData(getMarketplaceCartData);
            return;
          }
          this.getMarketplaceCart(this.marketplaceCheckoutId).then((getMarketplaceCartData) => {
            if (!getMarketplaceCartData || getMarketplaceCartData.channels.length == 0) {
              this.marketplaceCheckoutId = getMarketplaceCartData.id;
              this.checkoutData = this.convertResponesToCheckoutData(getMarketplaceCartData);
              return;
            }
            if (!this.marketplaceCheckoutId) return;
            this.shopGrapqhlService
              .marketplaceCartAssignUser(this.marketplaceCheckoutId)
              .pipe(take(1))
              .subscribe((marketplaceCartAssignUserData) => {
                this.marketplaceCheckoutId = marketplaceCartAssignUserData.id;
                this.checkoutData = this.convertResponesToCheckoutData(
                  marketplaceCartAssignUserData,
                );
              });
          });
        } else {
          this.marketplaceCheckoutId = getMarketplaceCartData.id;
        }
      });
    } else {
      if (this.marketplaceCheckoutId) {
        this.getMarketplaceCart(this.marketplaceCheckoutId).then((data) => {
          if (!data) {
            this.createMarketplaceCart();
            return;
          } else this.checkoutData = this.convertResponesToCheckoutData(data);
        });
      } else this.createMarketplaceCart();
    }
  }

  public addCart(channel: string, variantID: string, count: number): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (!this.marketplaceCheckoutId) return reject(false);
      try {
        this.shopGrapqhlService
          .addMarketplaceCart(this.marketplaceCheckoutId, channel, variantID, count)
          .pipe(take(1))
          .subscribe((data: any) => {
            this.checkoutData = this.convertResponesToCheckoutData(data);
            resolve(true);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  public updateCart(marketplaceCartLineUpdateId: string, quantity: number): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.shopGrapqhlService
          .updateMarketplaceCart(marketplaceCartLineUpdateId, quantity)
          .pipe(take(1))
          .subscribe((data) => {
            this.checkoutData = this.convertResponesToCheckoutData(data);
            resolve(true);
          });
      } catch (error) {
        console.error(error);
        reject(false);
      }
    });
  }

  public deleteCart(lineId: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.shopGrapqhlService
          .deleteMarketplaceCart(lineId)
          .pipe(take(1))
          .subscribe((data) => {
            this.checkoutData = this.convertResponesToCheckoutData(data);
            resolve(true);
          });
      } catch (error) {
        console.error(error);
        reject(false);
      }
    });
  }

  private getMarketplaceCart(id?: string): Promise<any> {
    return lastValueFrom(
      this.shopGrapqhlService.getMarketplaceCart(id).pipe(
        take(1),
        catchError((error) => {
          return of(undefined);
        }),
      ),
    );
  }

  private createMarketplaceCart(): void {
    this.shopGrapqhlService
      .createMarketplaceCart()
      .pipe(take(1))
      .subscribe((cart) => {
        if (this.marketplaceCheckoutId) {
          this.getMarketplaceCart(this.marketplaceCheckoutId).then((data) => {
            if (!data) this.marketplaceCheckoutId = cart.id;
          });
        } else {
          this.marketplaceCheckoutId = cart.id;
        }
      });
  }

  private convertResponesToCheckoutData(data: any): CheckoutData {
    if (!this.marketplaceCheckoutId)
      return {
        marketplaceCheckoutId: '',
        checkoutLine: new Array<CheckoutLine>(),
        subTotalPrices: 0,
        subTotalQuantity: 0,
      };
    const checkoutData = {
      marketplaceCheckoutId: '',
      checkoutLine: new Array<CheckoutLine>(),
      subTotalPrices: 0,
      subTotalQuantity: 0,
    };
    checkoutData.marketplaceCheckoutId = this.marketplaceCheckoutId;
    checkoutData.checkoutLine = data.channels;
    checkoutData.subTotalPrices = data.channels.reduce(
      (acc: any, item: any) => acc + item.totalPrice.amount,
      0,
    );
    checkoutData.subTotalQuantity = data.channels.reduce(
      (acc: any, item: any) =>
        acc + item.lines.reduce((acc2: any, item2: any) => acc2 + item2.quantity, 0),
      0,
    );
    return checkoutData;
  }
}
